'use client'

import ComparisonsHero from './ComparisonsHero'
import ComparisonThumbnail from './ComparisonThumbnail'
import ComparisonsPicker from './ComparisonsPicker'
import FilterableContainer from '@/components/content/common/FilterableContainer'
import { PageContextProvider } from '@/components/content/common/PageContext'
import { ComparisonsMenuContextProvider, type NavbarData } from '@/context/NavbarContext'
import { sortToFeaturedOrder } from '@/lib/utils'
import type {
  Category,
  Comparison,
  ComparisonAppsApp,
  App,
  AppCategoryRecommendation,
} from '@/payload-types'
import type { FeaturedSection } from '@/types/app'

type ComparisonsContainerProps = {
  comparisons: Comparison[]
  comparisonsFeaturedTagSections: FeaturedSection[]
  categories: Category[]
  comparisonsMenuContext: NavbarData['comparisonsMenu']
}

export function filterComparisons(comparisons: any[], selectedCategoryId: number | null) {
  if (selectedCategoryId === null) {
    return comparisons
  }

  const filteredComparisons = comparisons.filter((comparison) => {
    const matchingApps =
      comparison.comparisonApps?.docs?.filter((doc: ComparisonAppsApp) => {
        const app = doc.app as App
        return app.categoryRecommendations?.docs?.some((acr) => {
          const rec = acr as AppCategoryRecommendation
          const catId = typeof rec.category === 'object' ? rec.category?.id : rec.category
          return catId === selectedCategoryId
        })
      }) ?? []
    return matchingApps.length >= 2
  })

  return filteredComparisons
}

export default function ComparisonsContainer({
  comparisons,
  comparisonsFeaturedTagSections,
  categories,
  comparisonsMenuContext,
}: ComparisonsContainerProps) {
  const heroComparisons = sortToFeaturedOrder(comparisons)

  return (
    <PageContextProvider scrollOffset={145}>
      <ComparisonsHero heroComparisons={heroComparisons} />
      <FilterableContainer
        collection="comparisons"
        categories={categories}
        items={comparisons}
        featuredItems={comparisonsFeaturedTagSections}
        element={({ item, type }: { item: Comparison; type?: string }) => (
          <ComparisonThumbnail comparison={item} type={type} />
        )}
        filterItems={(items, filter) => filterComparisons(items, filter.id)}
        picker={
          <ComparisonsMenuContextProvider value={comparisonsMenuContext}>
            <ComparisonsPicker />
          </ComparisonsMenuContextProvider>
        }
        cols={2}
      />
    </PageContextProvider>
  )
}
